import { IActionRepeaterValue, TypographySelectValue } from '@makyoapp/makyo-common'
import { cn, CornerRadius, RawSpacingInput, Text } from '@makyoapp/makyo-tw-ui'
import { HoverEffectVariant } from '@makyoapp/makyo-tw-ui/dist/lib/utils/hover-effects'

import { CoreComponentAction, CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'
import { getSafely } from '@/shared/safely'

import { CompVariableText } from '../../shared/CompVariableText'

function trimEmptyEdges(nodes: any[]): any[] {
  if (!Array.isArray(nodes)) return nodes
  const isEmptyNode = (node: any) => {
    if (!node) return true

    if (Object.keys(node).length === 1 && 'text' in node && node.text === '') {
      return true
    }
    if (Object.keys(node).length === 1 && Array.isArray(node.children) && node.children.length === 0) {
      return true
    }

    if (
      Object.keys(node).length === 1 &&
      Array.isArray(node.children) &&
      node.children.length > 0 &&
      node.children.every(isEmptyNode)
    ) {
      return true
    }

    return false
  }

  let start = 0
  while (start < nodes.length && isEmptyNode(nodes[start])) {
    start++
  }

  let end = nodes.length - 1
  while (end >= start && isEmptyNode(nodes[end])) {
    end--
  }

  return nodes.slice(start, end + 1).map((node) => {
    if (
      node &&
      Array.isArray(node.children) &&
      node.children.length > 0 &&
      !(node.children.length === 1 && 'text' in node.children[0] && node.children[0].text === '')
    ) {
      return {
        ...node,
        children: trimEmptyEdges(node.children),
      }
    }
    return node
  })
}

export interface CompTextWrapperProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
}

/**
 * CompTextWrapperCore
 */
export const CompTextWrapperCore = ({
  infos: { component, settings, containerClient, currentParentBg, detailTableId, detailRowIndex, rowId: detailRowId },
  action: { simulateAction },
}: CompTextWrapperProps) => {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  // ========================= data =========================
  const text = settings[ComponentSettingKey.TextDataValue] ?? []

  // check if text empty
  const currentText = useParseVariableText(text, containerClient, detailTableId, detailRowIndex, detailRowId)

  // ========================= design =========================

  // base
  const width = getSafely(settings, [ComponentSettingKey.TextDesignWidth], ['100%'])[0]
  const alignment = settings[ComponentSettingKey.TextDesignCompHAlign] ?? 'parent'
  const padding = settings[ComponentSettingKey.TextDesignPadding] as RawSpacingInput
  const margin = settings[ComponentSettingKey.TextDesignMargin] as RawSpacingInput

  // text
  const borderRadius = settings[ComponentSettingKey.TextDesignCornerRadius] as CornerRadius

  const border = (settings[ComponentSettingKey.TextDesignBorder] as string[]) ?? []
  const borderWidth = settings[ComponentSettingKey.TextDesignBorderWidth]
  const borderStyle = settings[ComponentSettingKey.TextDesignBorderStyle]
  const effectiveBorderStyle = borderStyle ?? 'solid'
  const effectiveBorderWidth = borderWidth?.[0] ?? 0
  const hideTopBorder = !border.includes('top')
  const hideRightBorder = !border.includes('inline-end')
  const hideBottomBorder = !border.includes('bottom')
  const hideLeftBorder = !border.includes('inline-start')

  const textAlign = settings[ComponentSettingKey.TextDesignHAlign] || 'start'

  const textTypo = (settings[ComponentSettingKey.TextDesignTypography] as TypographySelectValue) ?? {
    typography: 'Body Large',
  }

  const textColor = settings[ComponentSettingKey.TextDesignColor] || 'adaptive'
  const textColorSolid = settings[ComponentSettingKey.TextDesignColorSolid] || 'primary-40'
  const textColorStartGradient = settings[ComponentSettingKey.TextDesignColorStartGradient] || 'primary-40'
  const textColorEndGradient = settings[ComponentSettingKey.TextDesignColorEndGradient] || 'primary-40'
  const textColorGradientDirection = settings[ComponentSettingKey.TextDesignColorGradientDirection]

  const hoverEffect = settings[ComponentSettingKey.TextDesignHoverEffect] as HoverEffectVariant

  // ========================= actions =========================

  // Get click actions from settings
  const clickActions = settings?.[ComponentSettingKey.TextActionClick] as IActionRepeaterValue[]
  const hasActions = clickActions && clickActions.length > 0

  // Click handler function
  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
    if (hasActions) {
      simulateAction({ actions: clickActions })
    }
  }

  // ========================= extra =========================

  if (!currentText || !currentText.trim().length) return null

  // ========================= render =========================
  return (
    <Text
      // base
      id={component._id}
      width={width}
      alignment={alignment}
      padding={padding}
      margin={margin}
      backgroundColor={currentParentBg}
      devMode={isDevelopmentMode}
      // text
      textAlign={textAlign}
      borderRadius={borderRadius}
      typography={textTypo}
      hideTopBorder={hideTopBorder}
      hideRightBorder={hideRightBorder}
      hideBottomBorder={hideBottomBorder}
      hideLeftBorder={hideLeftBorder}
      borderWidth={effectiveBorderWidth}
      borderStyle={effectiveBorderStyle}
      // color
      textColor={textColor}
      textColorSolid={textColorSolid}
      textColorStartGradient={textColorStartGradient}
      textColorEndGradient={textColorEndGradient}
      textColorGradientDirection={textColorGradientDirection}
      // hover effect
      hoverEffect={hoverEffect}
      hoverIntensity='md'
      // action
      onClick={handleClick}
    >
      <CompVariableText
        nodes={trimEmptyEdges(text)}
        containerClient={containerClient}
        tableId={detailTableId}
        rowIndex={detailRowIndex}
        rowId={detailRowId}
        textProps={{ style: { whiteSpace: 'pre-wrap', textAlign: textAlign ?? 'center' } }}
        className={cn('mb-3 last:mb-0')}
      />
    </Text>
  )
}
